import type { Coordinate } from '@orgsim/shared';

export type MarkerPoint = { id: string; x: number; y: number; radius: number };

export function moveFromDisplay(
  actual: Coordinate,
  displayed: Coordinate,
  target: Coordinate,
): Coordinate {
  return {
    x: Math.round((actual.x + target.x - displayed.x) * 100) / 100,
    y: Math.round((actual.y + target.y - displayed.y) * 100) / 100,
  };
}

// Spread-layout radius of an icon: the farthest corner from its anchor, since icons with a
// caption or side text are anchored off-centre.
export function iconRadius(width: number, height: number, [x, y]: [number, number]): number {
  return Math.ceil(Math.hypot(Math.max(x, width - x), Math.max(y, height - y)));
}

// Layout changes only display positions. The scenario coordinates remain untouched.
export function arrangeMarkerPoints(
  points: MarkerPoint[],
): Record<string, { x: number; y: number }> {
  const groups: MarkerPoint[][] = [];
  for (const point of [...points].sort((a, b) => a.id.localeCompare(b.id))) {
    const touching = groups.filter((group) =>
      group.some(
        (other) =>
          Math.hypot(point.x - other.x, point.y - other.y) < point.radius + other.radius + 12,
      ),
    );
    const merged = [point, ...touching.flat()];
    for (const group of touching) groups.splice(groups.indexOf(group), 1);
    groups.push(merged);
  }

  const result: Record<string, { x: number; y: number }> = {};
  for (const group of groups) {
    if (group.length === 1) {
      const point = group[0];
      if (point) result[point.id] = { x: point.x, y: point.y };
      continue;
    }
    group.sort((a, b) => a.id.localeCompare(b.id));
    const center = {
      x: group.reduce((sum, p) => sum + p.x, 0) / group.length,
      y: group.reduce((sum, p) => sum + p.y, 0) / group.length,
    };
    const diameter = Math.max(...group.map((p) => p.radius)) * 2 + 18;
    const radius = Math.max(58, diameter / (2 * Math.sin(Math.PI / group.length)));
    const columns = Math.ceil(Math.sqrt(group.length));
    group.forEach((point, i) => {
      if (group.length <= 8) {
        const angle = -Math.PI / 2 + (i * Math.PI * 2) / group.length;
        result[point.id] = {
          x: center.x + Math.cos(angle) * radius,
          y: center.y + Math.sin(angle) * radius,
        };
      } else {
        result[point.id] = {
          x: center.x + ((i % columns) - (columns - 1) / 2) * diameter,
          y:
            center.y +
            (Math.floor(i / columns) - (Math.ceil(group.length / columns) - 1) / 2) * diameter,
        };
      }
    });
  }
  // Reserve isolated objects before resolving collisions between expanded groups.
  const ordered = [...points].sort((a, b) => {
    const aFixed = result[a.id]?.x === a.x && result[a.id]?.y === a.y;
    const bFixed = result[b.id]?.x === b.x && result[b.id]?.y === b.y;
    return Number(bFixed) - Number(aFixed) || a.id.localeCompare(b.id);
  });
  const placed: MarkerPoint[] = [];
  for (const point of ordered) {
    const desired = result[point.id] ?? point;
    let candidate = desired;
    const overlaps = (position: { x: number; y: number }) =>
      placed.some(
        (p) => Math.hypot(position.x - p.x, position.y - p.y) < point.radius + p.radius + 12,
      );
    const step = point.radius * 2 + 18;
    let ring = 1;
    while (overlaps(candidate)) {
      let found = false;
      for (let x = -ring; x <= ring && !found; x++) {
        for (let y = -ring; y <= ring; y++) {
          if (Math.abs(x) !== ring && Math.abs(y) !== ring) continue;
          const next = { x: desired.x + x * step, y: desired.y + y * step };
          if (!overlaps(next)) {
            candidate = next;
            found = true;
            break;
          }
        }
      }
      ring++;
    }
    result[point.id] = candidate;
    placed.push({ ...point, ...candidate });
  }
  return result;
}
